import { Editor } from '@tinymce/tinymce-react';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import 'tinymce/skins/ui/oxide/skin.css';

interface IProps {
    content: string;
    setContent: (content: string) => void;
    /* View only - no toolbar and no typing, for notes recorded on a closed job (MMG-4345) */
    readOnly?: boolean;
}

const allowedTags = [
    'a',
    'b',
    'i',
    'u',
    'strong',
    'em',
    'p',
    'ul',
    'ol',
    'li',
    'br',
    'hr',
    'span',
    'div',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5'
];

const getUniqueTags = (m: string) => {
    if (!m) return [];

    // Parse HTML string
    const parser = new DOMParser();
    const doc = parser.parseFromString(m, 'text/html');

    // Get all elements and their tag names
    const elements = doc.body.getElementsByTagName('*');
    const tagNames = Array.from(elements).map(el => el.tagName.toLowerCase());

    // Filter out duplicates
    return [...new Set(tagNames)];
};

const sanitizeContent = (m: string) => {
    if (!m) return m;
    let sanitizedContent = m;
    const uniqueTags = getUniqueTags(sanitizedContent);
    const invalidTags = uniqueTags.filter(tag => !allowedTags.includes(tag));
    if (invalidTags.length) {
        // remove invalid tags from the content such as tables, etc
        invalidTags.forEach(tag => {
            const regex = new RegExp(`</?${tag}[^>]*>`, 'g');
            sanitizedContent = sanitizedContent.replace(regex, '');
        });
    }
    // Remove all HTML comment tags
    sanitizedContent = sanitizedContent.replace(/<!--[\s\S]*?-->/g, '');
    // Remove all <script> tags and their content
    sanitizedContent = sanitizedContent.replace(/<script[^>]*>([\s\S]*?)<\/script>/gi, '');
    // Remove all <style> tags and their content
    sanitizedContent = sanitizedContent.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, '');
    // Remove all inline style attributes
    // sanitizedContent = sanitizedContent.replace(/\s*style="[^"]*"/gi, '');
    // Remove all inline class attributes
    sanitizedContent = sanitizedContent.replace(/\s*class="[^"]*"/gi, '');
    // Strip away all spaces and extra lines between tags
    sanitizedContent = sanitizedContent.replace(/>\s+</g, '><').replace(/\s+/g, ' ').trim();
    // log
    if (sanitizedContent.length < m.length) {
        if (invalidTags.length)
            console.log(
                'sanitized',
                m.length,
                'characters down to',
                sanitizedContent.length,
                'invalid tags removed',
                invalidTags
            );
        else console.log('sanitized', m.length, 'characters down to', sanitizedContent.length);
    }
    return sanitizedContent;
};

const RTE = ({ content, setContent, readOnly = false }: IProps) => {
    const id = useMemo(() => uuidv4(), []);
    const [isLoaded, setIsLoaded] = useState(false);

    let requiresSanitization = false;

    const onkeyup = () => {
        try {
            const tinyMCEInstance = (window as any).tinymce.get(id);
            if (tinyMCEInstance && tinyMCEInstance.initialized) {
                const editorContent = tinyMCEInstance.getContent();
                if (!requiresSanitization) {
                    setContent(editorContent || '');
                } else {
                    requiresSanitization = false;
                    const sanitizedContent = sanitizeContent(editorContent || '');
                    setContent(sanitizedContent);
                }
            }
        } catch (error) {
            console.error('Error in onkeyup handler:', error);
        }
    };

    useEffect(() => {
        // Only load TinyMCE when component mounts
        const loadTinyMCE = async () => {
            try {
                // Dynamic imports - loads only what's needed
                await import('tinymce/tinymce');
                await import('tinymce/models/dom/model');
                await import('tinymce/themes/silver');
                await import('tinymce/icons/default');
                // Import only the plugins you actually use
                await import('tinymce/plugins/advlist');
                await import('tinymce/plugins/anchor');
                await import('tinymce/plugins/autolink');
                await import('tinymce/plugins/autoresize');
                await import('tinymce/plugins/autosave');
                await import('tinymce/plugins/charmap');
                await import('tinymce/plugins/code');
                await import('tinymce/plugins/codesample');
                await import('tinymce/plugins/directionality');
                await import('tinymce/plugins/emoticons');
                await import('tinymce/plugins/fullscreen');
                await import('tinymce/plugins/help');
                await import('tinymce/plugins/help/js/i18n/keynav/en');
                await import('tinymce/plugins/image');
                await import('tinymce/plugins/importcss');
                await import('tinymce/plugins/insertdatetime');
                await import('tinymce/plugins/link');
                await import('tinymce/plugins/lists');
                await import('tinymce/plugins/media');
                await import('tinymce/plugins/nonbreaking');
                await import('tinymce/plugins/pagebreak');
                await import('tinymce/plugins/preview');
                await import('tinymce/plugins/quickbars');
                await import('tinymce/plugins/save');
                await import('tinymce/plugins/searchreplace');
                await import('tinymce/plugins/table');
                await import('tinymce/plugins/visualblocks');
                await import('tinymce/plugins/visualchars');
                await import('tinymce/plugins/wordcount');

                setIsLoaded(true);
            } catch (error) {
                console.error('Failed to load TinyMCE:', error);
            }
        };

        loadTinyMCE();
    }, [id]); // Removed content dependency to prevent re-initialization

    // Separate useEffect to handle content prop changes
    useEffect(() => {
        if (isLoaded) {
            const tinyMCEInstance = (window as any).tinymce.get(id);
            if (tinyMCEInstance && tinyMCEInstance.initialized) {
                try {
                    const currentContent = tinyMCEInstance.getContent();
                    // Normalize empty content for comparison
                    const normalizedCurrent =
                        currentContent === '<p></p>' || currentContent === '<p><br></p>' ? '' : currentContent;
                    const normalizedContent = content || '';

                    if (normalizedCurrent !== normalizedContent) {
                        tinyMCEInstance.setContent(content || '');
                    }
                } catch (error) {
                    console.warn('TinyMCE not ready for content update, skipping:', error);
                }
            }
        }
    }, [content, isLoaded, id]);

    if (!isLoaded) return <div>Loading editor...</div>;

    return (
        <Editor
            id={id}
            disabled={readOnly}
            init={{
                promotion: false,
                skin: false,
                content_css: false,
                readonly: readOnly,
                plugins: 'lists link',
                toolbar: readOnly ? false : 'link unlink bold italic underline bullist numlist fontsize',
                toolbar_mode: 'scrolling',
                menubar: false,
                relative_urls: false,
                branding: false,
                width: '100%',
                content_style: `
                                body {
                                    font-family: Roboto, sans-serif;
                                }
                            `,

                init_instance_callback: editor => {
                    editor.setContent(content);
                    // Mark editor as initialized
                    editor.initialized = true;
                },
                setup(editor) {
                    editor.on('keyup', onkeyup);
                    editor.on('paste', () => (requiresSanitization = true));
                    editor.on('drop', () => {
                        requiresSanitization = true;
                        setTimeout(() => onkeyup(), 100);
                    });
                }
            }}
            apiKey="gpl"
        />
    );
};
export default RTE;
